import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Intro from './Intro';
import ProjectSummary from './ProjectSummary';
import Profile from './Profile';
import Footer from 'components/Footer';
import { usePrefersReducedMotion, useRouteTransition } from 'hooks';

import iphone11 from 'assets/iphone-11.glb';
import macbookPro from 'assets/macbook-pro.glb';
import nlogn from 'assets/nlogn.jpg';
import algo1 from 'assets/algo1.png';
import algo2 from 'assets/algo2.png';
import bird from 'assets/bird.png';
import news from 'assets/2.jpeg';
import news1 from 'assets/3.jpeg';




const disciplines = ['Designer', 'Creator', 'Author', 'Musician'];

export default function Home(props) {
  const { status } = useRouteTransition();
  const { hash, state } = useLocation();
  const initHash = useRef(true);
  const [visibleSections, setVisibleSections] = useState([]);
  const [scrollIndicatorHidden, setScrollIndicatorHidden] = useState(false);
  const intro = useRef();
  const projectOne = useRef();
  const projectTwo = useRef();
  const projectThree = useRef();
  const projectFour = useRef();
  const about = useRef();
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const revealSections = [intro, projectOne, projectTwo, projectThree, about];

    const sectionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target;
          observer.unobserve(section);
          if (visibleSections.includes(section)) return;
          setVisibleSections(prevSections => [...prevSections, section]);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });

    const indicatorObserver = new IntersectionObserver(([entry]) => {
      setScrollIndicatorHidden(!entry.isIntersecting);
    }, { rootMargin: '-100% 0px 0px 0px' });

    revealSections.forEach(section => {
      sectionObserver.observe(section.current);
    });

    indicatorObserver.observe(intro.current);

    return () => {
      sectionObserver.disconnect();
      indicatorObserver.disconnect();
    };
  }, [visibleSections]);

  useEffect(() => {
    const hasEntered = status === 'entered';
    const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;
    let scrollObserver;
    let scrollTimeout;

    const handleHashchange = (hash, scroll) => {
      clearTimeout(scrollTimeout);
      const hashSections = [intro, projectOne, projectTwo, projectThree, about];
      const hashString = hash.replace('#', '');
      const element = hashSections.filter(item => item.current.id === hashString)[0];
      if (!element) return;
      const behavior = scroll && !prefersReducedMotion ? 'smooth' : 'instant';
      const top = element.current.offsetTop;

      scrollObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          scrollTimeout = setTimeout(() => {
            element.current.focus();
          }, prefersReducedMotion ? 0 : 400);
          observer.unobserve(entry.target);
        }
      }, { rootMargin: '-20% 0px -20% 0px' });

      scrollObserver.observe(element.current);

      if (supportsNativeSmoothScroll) {
        window.scroll({
          top,
          left: 0,
          behavior,
        });
      } else {
        window.scrollTo(0, top);
      }
    };

    if (hash && initHash.current && hasEntered) {
      handleHashchange(hash, false);
      initHash.current = false;
    } else if (!hash && initHash.current && hasEntered) {
      window.scrollTo(0, 0);
      initHash.current = false;
    } else if (hasEntered) {
      handleHashchange(hash, true);
    }

    return () => {
      clearTimeout(scrollTimeout);
      if (scrollObserver) {
        scrollObserver.disconnect();
      }
    };
  }, [hash, state, prefersReducedMotion, status]);

  return (
    <Fragment>
      <Helmet>
        <title>Aditya's Portfolio</title>
        <meta
          name="description"
          content="Portfolio of Aditya K. – a designer, full-stack developer, and creator of web & mobile solutions with a focus on motion and user experience."
        />
        <link rel="prefetch" href={iphone11} as="fetch" crossorigin="" />
        <link rel="prefetch" href={macbookPro} as="fetch" crossorigin="" />
      </Helmet>
      <Intro
        id="intro"
        sectionRef={intro}
        disciplines={disciplines}
        scrollIndicatorHidden={scrollIndicatorHidden}
      />
      <ProjectSummary
        id="project-1"
        sectionRef={projectOne}
        visible={visibleSections.includes(projectOne.current)}
        index={1}
        title="NlogN"
        description="Building a peer community to enhance the coding skills."
        buttonText="Visit Website"
        buttonLink="https://nlogn420.netlify.app/"
        model={{
          type: 'laptop',
          alt: 'NlogN Landing Page',
          textures: [
            {
              src: nlogn,
              srcSet: `${nlogn} 800w, ${nlogn} 1440w`,
              placeholder: nlogn,
            },
          ],
        }}
      />
      <ProjectSummary
        id="project-2"
        alternate
        sectionRef={projectTwo}
        visible={visibleSections.includes(projectTwo.current)}
        index={2}
        title="AlgoMaster"
        description="Data Structures & Algorithms Guide for Coding enthusiasts."
        buttonText="View Application"
        buttonLink="https://play.google.com/store/apps/details?id=com.algomaster.app"
        model={{
          type: 'phone',
          alt: 'AlgoMaster screens.',
          textures: [
            {
              src: algo1,
              srcSet: `${algo1} 254w, ${algo1} 508w`,
              placeholder: algo1,
            },
            {
              src: algo2,
              srcSet: `${algo2} 254w, ${algo2} 508w`,
              placeholder: algo2,
            },
          ],
        }}
      />
       <ProjectSummary
        id="project-3"
        sectionRef={projectThree}
        visible={visibleSections.includes(projectThree.current)}
        index={3}
        title="AI Powered Flappy Bird"
        description="This Project uses neural-network & genetic algorithm for bird's brain."
        buttonText="View Project"
        buttonTo=""
        model={{
          type: 'laptop',
          alt: 'AI Bird',
          textures: [
            {
              src: bird,
              srcSet: `${bird} 800w, ${bird} 1440w`,
              placeholder: bird,
            },
          ],
        }}
      />
      <ProjectSummary
        id="project-4"
        alternate
        sectionRef={projectFour}
        visible={visibleSections.includes(projectThree.current)}
        index={4}
        title="News Today"
        description="News App Consuming API from NewsAPI.org"
        buttonText="View Application"
        buttonLink="https://play.google.com/store/apps/details?id=com.freejob.alert2020"
        model={{
          type: 'phone',
          alt: 'News App screens.',
          textures: [
            {
              src: news,
              srcSet: `${news} 254w, ${news} 508w`,
              placeholder: news,
            },
            {
              src: news1,
              srcSet: `${news1} 254w, ${news1} 508w`,
              placeholder: news1,
            },
          ],
        }}
      />
     
      <Profile
        sectionRef={about}
        visible={visibleSections.includes(about.current)}
        id="about"
      />
      <Footer />
    </Fragment>
  );
}
